import { baseState, initialStateByModule } from './state'
import { getCountries } from '@/api/services'
import { getAppSettings, postLead, patchLead } from '../api/services'

const SET_APP_SETTINGS = 'setAppSettings'
const SET_DESTINATIONS = 'setDestinations'
const CHANGE_THEME = 'changeTheme'

// Lead
const RESET_LEAD = 'resetLead'
const CHANGE_LEAD_UUID = 'changeLeadUuid'
const CHANGE_LEAD_NAME = 'changeLeadName'
const CHANGE_LEAD_EMAIL = 'changeLeadEmail'
const CHANGE_LEAD_PHONE = 'changeLeadPhone'
const CHANGE_LINK = 'changeLink'
const CHANGE_URL = 'changeUrl'
const CHANGE_INITIAL_DATE = 'changeInitialDate'
const CHANGE_END_DATE = 'changeEndDate'
const CHANGE_DESTINATION = 'changeDestination'
const CHANGE_COUNTRIES = 'changeCountries'
const CHANGE_TRAVEL_TYPE = 'changeTravelType'

const state = initialStateByModule('travelSetup')

const getters = {
  brasilTrip (state) {
    return state.listDestinations.find(destination => destination.label === 'Brasil')
  },
  getLeadInfo (state, getters, rootState) {
    const { result, selectedQuote, promoCode } = rootState.quotation
    const amount = (result[selectedQuote] && result[selectedQuote].totalValue) || null

    try {
      return {
        cookieGA: state.params._ga,
        data: {
          leadName: state.lead.leadName,
          leadEmail: state.lead.leadEmail,
          leadPhone: state.lead.leadPhone,
          link: state.lead.link,
          url: rootState.quotation.quotationUrl,
          type: state.lead.type,
          utmCampaign: state.params.utm_campaign,
          utmMedium: state.params.utm_medium,
          utmSource: state.params.utm_source,
          initialDate: rootState.quotation.initialDate,
          endDate: rootState.quotation.endDate,
          ...!!state.lead.destination && { destination: state.lead.destination },
          travelType: state.lead.travelType,
          destinations: state.lead.travelType === 'SINGLE_TRIP'
            ? state.countries.map(({ label, continent }) => ({
              countryName: label,
              destination: continent
            }))
            : [{ destination: state.lead.destination }],
          ...(promoCode.valid && promoCode.value && { promocode: promoCode.value }),
          ...(amount && { amount })
        }
      }
    } catch (error) {
      this._vm.$sentry.logWithContext({
        ctx: {
          label: 'getLeadInfo',
          error
        },
        error
      })
    }
  },
  internationalDestinations (state, getters) {
    return state.listDestinations.filter(country => country.id !== getters.brasilTrip.id)
  }
}

const mutations = {
  [SET_APP_SETTINGS] (state, payload) {
    state.appSettings = payload
  },
  [CHANGE_THEME] (state, payload) {
    state.theme = payload
  },
  [SET_DESTINATIONS] (state, payload) {
    state.listDestinations = payload
  },
  [RESET_LEAD] (state) {
    state.lead = baseState.travelSetup.lead
  },
  [CHANGE_LEAD_UUID] (state, payload) {
    state.lead.uuid = payload
  },
  [CHANGE_LEAD_NAME] (state, payload) {
    state.lead.leadName = payload
  },
  [CHANGE_LEAD_EMAIL] (state, payload) {
    state.lead.leadEmail = payload
  },
  [CHANGE_LEAD_PHONE] (state, payload) {
    state.lead.leadPhone = payload
  },
  [CHANGE_LINK] (state, payload) {
    state.lead.link = payload
  },
  [CHANGE_URL] (state, payload) {
    state.lead.url = payload
  },
  [CHANGE_INITIAL_DATE] (state, payload) {
    state.lead.initialDate = payload
  },
  [CHANGE_END_DATE] (state, payload) {
    state.lead.endDate = payload
  },
  [CHANGE_DESTINATION] (state, payload) {
    state.lead.destination = payload
  },
  [CHANGE_TRAVEL_TYPE] (state, payload) {
    state.lead.travelType = payload
  },
  [CHANGE_COUNTRIES] (state, payload) {
    state.countries = payload
    state.lead.countries = payload
  }
}

function resetAndPopulateStorage (data) {
  const CICLIC_TRAVEL_LEAD = 'ciclic:travel:lead'

  window.sessionStorage.getItem(CICLIC_TRAVEL_LEAD) && window.sessionStorage.removeItem(CICLIC_TRAVEL_LEAD)

  window.sessionStorage.setItem(CICLIC_TRAVEL_LEAD, JSON.stringify(data))
}

const actions = {
  async init ({ state, commit }) {
    try {
      const appSetting = await getAppSettings(state.params.client_id)

      commit(SET_APP_SETTINGS, {
        ...appSetting.global,
        ...appSetting.product.travel,
        hasLoaded: true
      })
    } catch (error) {
      this._vm.$sentry.logWithContext({
        ctx: {
          label: `[init/getAppSettings]: error client_id ${state.params.client_id}`,
          error
        },
        error
      })

      commit(SET_APP_SETTINGS, { hasLoaded: true })
    }
  },
  async getListDestinations ({ commit }) {
    await getCountries()
      .then(response => {
        commit(SET_DESTINATIONS, response)
      })
      .catch(error => {
        this._vm.$sentry.logWithContext({
          ctx: {
            label: 'getListDestinations',
            error
          },
          error
        })
      })
  },
  async postLeadAction ({ rootState, getters, commit }, { allowTermsUse }) {
    try {
      if (rootState.travelSetup.lead.uuid) return

      let _getContract = await getters.getLeadInfo
      _getContract.data.link = window.location.href
      _getContract.data.allowTermsUse = allowTermsUse

      const _res = await postLead(_getContract)

      resetAndPopulateStorage(_res.data)
      commit(CHANGE_LEAD_UUID, _res.data.uuid)
    } catch (error) {
      this._vm.$sentry.logWithContext({
        ctx: {
          label: 'PostLeadAction',
          error
        },
        error
      })
    }
  },
  async patchLeadAction ({ rootState, getters, commit }, step) {
    try {
      const _getContract = await getters.getLeadInfo
      _getContract.uuid = rootState.travelSetup.lead.uuid
      _getContract.data.link = window.location.href
      _getContract.data.step = step

      const _res = await patchLead(_getContract)

      if (!rootState.travelSetup.lead.uuid) {
        clearInterval(window.leadInterval)
        return
      }

      resetAndPopulateStorage(_res.data)
      commit(CHANGE_LEAD_UUID, _res.data.uuid)
      commit(CHANGE_LEAD_NAME, _res.data.leadName)
      commit(CHANGE_LEAD_EMAIL, _res.data.leadEmail)
      commit(CHANGE_LEAD_PHONE, Number(_res.data.leadPhone.replace(/\D/g, '')))
    } catch (error) {
      rootState.travelSetup.uuid = ''
      this._vm.$sentry.logWithContext({
        ctx: {
          label: 'PatchLeadAction',
          error
        },
        error
      })
    }
  },
  getUserData ({ commit }) {
    try {
      const _user = JSON.parse(
        window.sessionStorage.getItem('@ciclic-journey:user')
      )

      if (_user) {
        commit(CHANGE_LEAD_NAME, _user.name)
        commit(CHANGE_LEAD_EMAIL, _user.email)
        commit(CHANGE_LEAD_PHONE, _user.phone && Number(_user.phone.replace(/\D/g, '')))
      }
    } catch (error) {
      this._vm.$sentry.logWithContext({
        ctx: {
          label: 'getUserData',
          error
        },
        error
      })
    }
  }
}

export default {
  namespaced: true,
  getters,
  state,
  mutations,
  actions
}
